import { keyBy, map, mapValues } from 'lodash';
import { useDispatch } from 'react-redux';
import { useEffect, useMemo } from 'react';

import agentsResource from 'src/actions/agent/agentsResource';
import customChannelResource from 'src/actions/channels/customChannelResource';
import routingGroupsResource from 'src/actions/routingGroups/routingGroupsResource';
import { toastError } from 'src/actions/toast';
import { toDateTimeString } from 'src/presentation/components/pages/shared/utils/TimeUtils';
import useResource, {
  getConsolidatedState,
  someFailed,
  STATUS_FAILED,
  STATUS_SUCCEEDED,
} from 'src/presentation/hooks/useResource';

const useCustomChannel = () => {
  const dispatch = useDispatch();

  const { resetErrors } = customChannelResource.actions;

  const { values: agents, loadingState: agentsLoadingState } = useResource(agentsResource);
  const {
    errors: customChannelsErrors,
    loadingState: customChannelsLoadingState,
    values: customChannels,
  } = useResource(customChannelResource);

  const { loadingState: routingGroupsLoadingState } = useResource(routingGroupsResource);

  const usersMap = useMemo(() => mapValues(keyBy(agents, 'id')), [agents]);

  const loadingStates = [agentsLoadingState, customChannelsLoadingState, routingGroupsLoadingState];

  const fetchError = someFailed(...loadingStates);

  const loadingState = fetchError ? STATUS_FAILED : getConsolidatedState(loadingStates);

  const customChannelsExtended = useMemo(() => {
    if (customChannelsLoadingState !== STATUS_SUCCEEDED) {
      return [];
    }
    return map(customChannels, customChannel => ({
      ...customChannel,
      updatedAtString: toDateTimeString(customChannel.updatedAt),
      updatedByString: usersMap[customChannel?.updatedBy?.userId]?.name,
    }));
  }, [customChannels, customChannelsLoadingState, usersMap]);

  useEffect(() => {
    if (fetchError) {
      dispatch(toastError('Error while loading data. Please wait and try refreshing the page'));
    }
  }, [fetchError, dispatch]);

  return {
    customChannels: customChannelsExtended,
    errors: customChannelsErrors,
    loadingState,
    resetErrors,
    usersMap,
  };
};

export default useCustomChannel;
